import {useState, useEffect, useRef, forwardRef} from 'react'
import {getBadgeDetails, getAllBadges} from '@cssbattle/shared/badges'
import {HStack, VStack} from '@components/Stack'
import {Text} from '@components/Text'
import ParticleBlast from '../particleBlast'
import {motion} from 'framer-motion'
import {getHumanReadableDate} from 'src/utils'
import {Modal} from '@components/Modal'
import {useUser} from 'src/hooks/useUser'
import {BADGE_TYPES} from '@cssbattle/shared/constants'

/**
 * Badge component to display a user's badge
 * @param {Object} props - Component props
 * @param {string} props.badgeId - Badge ID
 * @param {Object} props.metadata - Badge metadata
 * @param {string} props.size - Badge size (small, medium, large)
 * @param {boolean} props.showTooltip - Whether to show the tooltip on hover
 * @returns {JSX.Element} Badge component
 */
export const Badge = forwardRef(
  (
    {
      badgeId,
      metadata = {},
      data,
      size = 'medium',
      showTooltip = true,
      particles = true,
      onClick,
    },
    ref
  ) => {
    const badgeDetails = getBadgeDetails(badgeId)
    const canvasRef = useRef()
    const imageRef = useRef()
    const particleBlastRef = useRef(null)

    useEffect(() => {
      const w = window.innerWidth
      const h = window.innerHeight

      if (w < 768 || !particles) return

      // Create canvas element
      const canvas = document.createElement('canvas')
      canvas.classList.add('badge-particle-canvas')
      document.body.appendChild(canvas)
      canvasRef.current = canvas

      canvas.setAttribute('width', w)
      canvas.setAttribute('height', h)
      const ctx = canvas.getContext('2d')
      const imageRect = imageRef?.current?.getBoundingClientRect() || {}

      // Create a new instance of ParticleBlast
      particleBlastRef.current = new ParticleBlast({
        maxParticles: 8,
        particleSizeFadeout: 0.93,
        particleAlphaStart: 0.85,
      })
      particleBlastRef.current.initialize(ctx, w, h, imageRect)

      return () => {
        // Clean up by stopping the animation and removing canvas
        if (particleBlastRef.current) {
          particleBlastRef.current.stop()
        }
        if (canvasRef.current) {
          document.body.removeChild(canvasRef.current)
        }
      }
    }, [])

    if (!badgeDetails) {
      return null
    }

    const sizeMap = {
      small: {
        size: '2rem',
        fontSize: '2.66rem',
      },
      medium: {
        size: '3rem',
        fontSize: '4rem',
      },
      large: {
        size: '3rem',
        fontSize: '4rem',
      },
    }

    const {size: width, fontSize} = sizeMap[size] || sizeMap.medium

    const handleMouseEnter = () => {
      // Use the ParticleBlast instance to spawn particles
      if (particleBlastRef.current) {
        particleBlastRef.current.spawnParticles([
          badgeDetails.particleColor || badgeDetails.color,
        ])
      }
    }

    const style = {
      width,
      fontSize,
    }
    // https://codepen.io/oliviale/pen/qpPByV
    return (
      <button
        className="badge-container"
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        ref={(el) => {
          imageRef.current = el
          if (ref) {
            ref(el)
          }
        }}
      >
        <div
          className={`badge ${badgeDetails.color}`}
          data-id={badgeDetails.id}
          style={style}
        >
          <div className="circle ">
            {badgeDetails.image ? (
              <img
                className="badge-image"
                src={`/images/badges/${badgeDetails.image}`}
                alt={badgeDetails.name}
              />
            ) : (
              <span className="badge-icon">{badgeDetails.icon}</span>
            )}
          </div>

          {badgeDetails.level ? (
            <div className="badge-level">{badgeDetails.level}</div>
          ) : null}
        </div>
        {showTooltip && (
          <div className="badge-tooltip">
            <VStack gap="0.5rem">
              <Text
                tag="h4"
                size={2}
                weight="bold"
                appearance="primary"
                mono={false}
              >
                {badgeDetails.name}
              </Text>
              <Text tag="p" size={1} mono={false} weight="500">
                {badgeDetails.description}
              </Text>
              {data ? (
                <Text
                  tag="p"
                  size={0}
                  mono={false}
                  weight="500"
                  appearance="secondary"
                >
                  Unlocked on{' '}
                  {getHumanReadableDate(data.awardedAt.toDate(), {
                    showTime: false,
                  })}
                </Text>
              ) : null}

              {metadata.rank && (
                <p className="badge-meta">Rank: {metadata.rank}</p>
              )}
              {metadata.levelId && (
                <p className="badge-meta">Target: #{metadata.levelId}</p>
              )}
            </VStack>
          </div>
        )}
      </button>
    )
  }
)

const MHStack = motion(HStack)
const MBadge = motion(Badge)

/**
 * BadgeList component to display a list of badges
 * @param {Object} props - Component props
 * @param {Array} props.badges - Array of badge objects
 * @param {string} props.size - Badge size (small, medium, large)
 * @param {boolean} props.showTooltip - Whether to show tooltips on hover
 * @returns {JSX.Element} BadgeList component
 */
export function BadgeList({badges = [], size = 'medium', showTooltip = true}) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const user = useUser()
  const allBadges = getAllBadges()
  const unlockedBadgeIds = new Set(badges.map((b) => b.badgeId))

  if (!badges.length) return null

  const containerVariants = {
    show: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: {opacity: 0, y: 10},
    show: {opacity: 1, y: 0},
  }

  const handleBadgeClick = () => {
    setIsModalOpen(true)
  }

  return (
    <>
      <MHStack
        gap="1.2rem"
        wrap="wrap"
        justify="center"
        variants={containerVariants}
        initial="hidden"
        animate="show"
      >
        {badges.map((badge) => (
          <MBadge
            key={badge.id}
            badgeId={badge.badgeId}
            metadata={badge.metadata}
            data={badge}
            size={size}
            showTooltip={showTooltip}
            variants={itemVariants}
            onClick={handleBadgeClick}
            style={{cursor: user ? 'pointer' : 'default'}}
          />
        ))}
      </MHStack>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        size="big"
        title="All Badges"
      >
        <VStack gap="2rem" align="stretch">
          <Text
            tag="h2"
            size={3}
            weight="bold"
            mono={false}
            appearance="primary"
          >
            Badges
          </Text>
          {[BADGE_TYPES.STREAK, BADGE_TYPES.SUBMISSION].map((badgeType) => (
            <VStack key={badgeType} gap="1rem" align="stretch" tag="section">
              <Text
                tag="h3"
                size={2}
                weight="bold"
                mono={false}
                transform="capitalize"
              >
                {badgeType}
              </Text>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: '1.5rem',
                  width: '100%',
                }}
              >
                {Object.values(allBadges)
                  .filter((badge) => badge.type === badgeType)
                  .map((badge) => {
                    const isUnlocked = unlockedBadgeIds.has(badge.id)
                    if (!badge.isEnabled) return null
                    return (
                      <div
                        key={badge.id}
                        style={{
                          filter: isUnlocked
                            ? 'none'
                            : 'grayscale(100%) brightness(0.5)',
                        }}
                      >
                        <Badge
                          badgeId={badge.id}
                          size="medium"
                          showTooltip={true}
                          particles={isUnlocked}
                        />
                      </div>
                    )
                  })}
              </div>
            </VStack>
          ))}
        </VStack>
      </Modal>
    </>
  )
}
